import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import type { ReactNode } from 'react';

import { Logger } from '../services';

// Define the shape of the context state
interface RulesState {
  isRulesEnabled: boolean;
  isMenuPinned: boolean;
}

// Define a generic action that automatically infers from RulesState
interface Action<K extends keyof RulesState> {
  type: K;
  value: RulesState[K];
}

// Initial state
export const initialState: RulesState = {
  isRulesEnabled: false,
  isMenuPinned: false,
};

// Reducer function that infers the key and value types
const reducer = <K extends keyof RulesState>(
  state: RulesState,
  action: Action<K>
): RulesState => ({ ...state, [action.type]: action.value });

// Create the context with a default value
const RulesContext = createContext<
  | {
      rulesState: RulesState;
      updateRulesState: <K extends keyof RulesState>(
        key: K,
        value: RulesState[K]
      ) => void;
    }
  | undefined
>(undefined);

// Provider component that wraps the children
export function RulesProvider({ children }: { children: ReactNode }) {
  const [rulesState, dispatch] = useReducer(reducer, initialState);

  // Function to dispatch actions based on key and value, with no hardcoding
  const updateRulesState = useCallback(
    <K extends keyof RulesState>(key: K, value: RulesState[K]) => {
      dispatch({ type: key, value }); // No hardcoded strings!
    },
    []
  );

  // Memoize the context value to avoid unnecessary re-renders
  const value = useMemo(
    () => ({
      rulesState,
      updateRulesState,
    }),
    [rulesState, updateRulesState]
  );

  return (
    <RulesContext.Provider value={value}>{children}</RulesContext.Provider>
  );
}

// Custom hook to use the Rules context
export const useRulesContext = () => {
  const context = useContext(RulesContext);

  if (!context) {
    Logger.warn(
      'useRulesContext not within a RulesProvider, returning default values.'
    );

    return {
      rulesState: initialState,
      updateRulesState: () => {},
    };
  }

  return context;
};
